<template>
  <div data-component-group="business-form" data-component="thread-submissions">
    <slot name="beforeForm" />

    <div v-if="!isDisabled" class="component-header">
      <v-autocomplete
        v-model="country"
        :items="countries"
        prepend-inner-icon="public"
        label="Country"
        item-title="name"
        item-value="code"
        :return-object="true"
        required
        :readonly="isDisabled"
        :error-messages="countryErrors"
        @blur="v$.country.$touch()"
      />

      <div v-if="!isDisabled" class="component-actions">
        <v-btn
          color="secondary"
          :loading="isLoading"
          @click="onUpdateThreadSubmissions">
          <v-icon>edit</v-icon>
          {{ triggerLabel }}
        </v-btn>
      </div>
    </div>
    <div class="component-body">
      <Removables
        v-if="hasThreadSubmissions"
        :items="threadSubmissions"
        :is-disabled="isDisabled"
        :on-remove="onRemoveThreadSubmission"
      >
        <template v-slot:item="scope" >
          <div class="d-flex align-center">
            <v-icon> public </v-icon>
            {{ scope.item.name }}
          </div>
        </template>
      </Removables>
    </div>
  </div>
</template>

<script>
  import { useVuelidate } from '@vuelidate/core'
  import { required } from '@vuelidate/validators';
  import Removables from '../../lists/Removables';
  import { isStr } from '../../../utils';
  // TODO: Translate component

  export default {
    components: {
      Removables,
    },
    setup () {
    return {
      v$: useVuelidate()
    }
  },
    validations() {
      return {
        country: {
          required,
        },
      };
    },
    props: {
      business: {
        type: Object,
        default: undefined,
      },
      triggerLabel: {
        type: String,
        default: 'Enable',
      },
      isDisabled: {
        type: Boolean,
        default: false,
      },
      onSubmit: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        country: null,
        threadSubmissions: [],
        isLoading: false,
      };
    },
    computed: {
      countries() {
        return this.$store.getters['users/countries'];
      },
      hasThreadSubmissions() {
        return this.threadSubmissions.length > 0;
      },
      countryErrors() {
        return this.v$.country.$errors.map(error => {
          if (error.$validator === 'required') {
            return 'Country is required';
          }
          return error.$message;
        });
      },
    },
    watch: {
      'business.id': {
        immediate: true,
        handler(nextId, prevId) {
          if (nextId) {
            if (prevId !== undefined) {
              this.clear();
            }
            this.getThreadSubmissions(this.business);
          }
        }
      },
    },
    methods: {
      onUpdateThreadSubmissions() {
        this.v$.$touch();

        if (!this.canSubmitRequest() || this.v$.$error || this.v$.pending) {
          return false;
        }

        this.isLoading = true;
        const { country, onSubmit } = this;
        onSubmit(country)
          .then(res => {
            this.threadSubmissions = res;
            this.getEnabledPromotions(this.business)
          })
          .catch(err => err)
          .finally(()=>{
            this.isLoading = false;
          });
      },
      getEnabledPromotions({ id }) {
        const query = {
          id,
          only_allowed_countries: true,
        };
        return this.$store.dispatch('businesses/getEnabledPromotions', query);
      },
      canSubmitRequest() {
        const { country } = this;

        return isStr(country?.code);
      },
      getThreadSubmissions({ id }) {
        const query = {
          id,
          thread_type: 1,
        };

        return this.$store.dispatch('businesses/getThreadSubmissions', query)
            .then(res => {
              this.threadSubmissions = res.data.data;
            });
      },
      onRemoveThreadSubmission(threadGroup) {
        const query = {
          id: this.business.id,
          country: threadGroup.code,
        };

        return this.$store.dispatch('businesses/removeThreadSubmission', query)
            .then(res => {
              this.threadSubmissions = res;
            });
      },
      clear() {
        this.v$.$reset();
        this.country = null;
      },
    },
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../../assets/styles/theme' as *;

[data-component-group='business-form'][data-component='thread-submissions'] {
  padding: $spacing-xl;
  background-color: map.get($colors, shades, 'white');
  border-top: 1px solid map.get($colors, grey, lighten-2);

  .component-actions {
    text-align: right;

    .v-btn {
      margin: 0;

      .v-icon {
        margin: 0 6px 0 0;
      }
    }
  }

  .labels {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-wrap: wrap;
  }

  .label {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: $text-size-sm;
    padding: 2px 6px;
    margin: 0 10px 10px 0;
    border-radius: $border-radius;
    color: map.get($colors, shades, 'white');
    background-color: map.get($colors, primary);

    .v-icon {
      font-size: $text-size-md;
      margin: 0 6px 0 0;
      color: map.get($colors, shades, 'white');
    }
  }

  [data-component='removables'] {
    padding: 16px 0 0 0;

    .v-list {
      padding: 0;
    }

    .v-list-item {
      height: 62px;
      border-radius: $border-radius;
      background-color: map.get($colors, grey, lighten-3);
    }
  }
}
</style>
